import axios from 'axios';
import { API_URL } from '../config';
import { normalizeToSecureUrl, secureRequestConfig } from './axiosSecurity';
import { translateKnownEnglishApiDetail } from '../utils/apiError';
import { clearAuthToken, getAuthToken } from '../utils/authToken';
import {
  Feedback,
  FeedbackListResponse,
  FeedbackPayload,
  FeedbackPromptConfig,
  FeedbackPromptConfigPayload,
  FeedbackStats,
  FeedbackAdminScopesResponse,
  FeedbackTargetsResponse,
  FeedbackWorkflowOption,
  KnowledgeArticle,
  KnowledgeArticleBatchImportResponse,
  KnowledgeArticleCategory,
  KnowledgeArticleEditorEntry,
  KnowledgeAuthorCorrelationResponse,
  KnowledgeCategoriesExportPayload,
  KnowledgeCategoriesImportPayload,
  KnowledgeCategoriesImportResponse,
  KnowledgeGlobalKbEditorEntry,
  ShouldPromptResponse,
  Opportunity,
  OpportunityDocumentListResponse,
  OpportunityFolderDeletePreview,
  OpportunityLinkablePurchase,
  OpportunityModuleCustomFieldValue,
  OpportunityDocument,
  OpportunityFolderTreeNode,
  User,
  UserListResponse,
  KnowledgeBaseAskResponse,
  KnowledgeBaseAssistantVisibilityResponse,
  PurchaseCustomFieldValue,
  WorkflowCustomField,
} from '../types';

const api = axios.create({
  baseURL: normalizeToSecureUrl(API_URL),
  // Perguntas ao assistente da KB podem demorar >30s no modelo; rotas específicas podem aumentar ainda mais.
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json; charset=utf-8',
  },
  responseType: 'json',
  responseEncoding: 'utf8',
});

// Garante upgrade para HTTPS quando a pagina estiver em HTTPS.
api.interceptors.request.use(secureRequestConfig);

// Interceptor para adicionar token em todas as requisições
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      clearAuthToken();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

export const authApi = {
  getMe: async (): Promise<User> => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },

  updateEmailNotificationPreferences: async (payload: {
    email_notify_ticket_opened?: boolean;
    email_notify_ticket_comment?: boolean;
    email_notify_ticket_closed?: boolean;
  }): Promise<User> => {
    const response = await api.put('/api/auth/me/email-notification-preferences', payload);
    return response.data;
  },
};

// Knowledge Base API

export const knowledgeBaseApi = {
  // Categories
  getCategories: async (): Promise<KnowledgeArticleCategory[]> => {
    const response = await api.get('/api/knowledge-base/categories');
    return response.data;
  },

  createCategory: async (category: { name: string; description?: string }): Promise<KnowledgeArticleCategory> => {
    const response = await api.post('/api/knowledge-base/categories', category);
    return response.data;
  },

  exportCategories: async (): Promise<KnowledgeCategoriesExportPayload> => {
    const response = await api.get('/api/knowledge-base/categories/export');
    return response.data;
  },

  importCategories: async (
    payload: KnowledgeCategoriesImportPayload,
    dryRun: boolean = true
  ): Promise<KnowledgeCategoriesImportResponse> => {
    const response = await api.post('/api/knowledge-base/categories/import', payload, {
      params: { dry_run: dryRun },
    });
    return response.data;
  },

  deleteCategory: async (id: number): Promise<void> => {
    await api.delete(`/api/knowledge-base/categories/${id}`);
  },

  // Articles
  getArticles: async (params?: {
    skip?: number;
    limit?: number;
    category_id?: number;
    search?: string;
    author?: string;
    sort_by?: 'updated_at' | 'created_at' | 'title' | 'author';
    sort_order?: 'asc' | 'desc';
    favorites_only?: boolean;
  }): Promise<KnowledgeArticle[]> => {
    const response = await api.get('/api/knowledge-base/articles', { params });
    return response.data;
  },

  getArticleAuthors: async (params?: { search?: string; limit?: number }): Promise<string[]> => {
    const response = await api.get('/api/knowledge-base/articles/authors', { params });
    return response.data;
  },

  getArticlesCount: async (params?: {
    category_id?: number;
    search?: string;
    author?: string;
    favorites_only?: boolean;
  }): Promise<{ total: number }> => {
    const response = await api.get('/api/knowledge-base/articles/count', { params });
    return response.data;
  },

  getArticle: async (id: number): Promise<KnowledgeArticle> => {
    const response = await api.get(`/api/knowledge-base/articles/${id}`);
    return response.data;
  },

  createArticle: async (article: {
    title: string;
    body: string;
    category_id: number;
  }): Promise<void> => {
    await api.post('/api/knowledge-base/articles', article);
  },

  // A categoria fica fora deste payload de propósito: ver updateArticleCategory.
  updateArticle: async (id: number, article: {
    title?: string;
    body?: string;
  }): Promise<void> => {
    await api.put(`/api/knowledge-base/articles/${id}`, article);
  },

  updateArticleCategory: async (id: number, categoryId: number): Promise<void> => {
    await api.put(`/api/knowledge-base/articles/${id}/category`, { category_id: categoryId });
  },

  deleteArticle: async (id: number): Promise<void> => {
    await api.delete(`/api/knowledge-base/articles/${id}`);
  },

  // Favorites
  toggleFavorite: async (articleId: number): Promise<{ is_favorite: boolean }> => {
    const response = await api.post(`/api/knowledge-base/articles/${articleId}/favorite`);
    return response.data;
  },

  getFavorites: async (params?: { skip?: number; limit?: number }): Promise<KnowledgeArticle[]> => {
    const response = await api.get('/api/knowledge-base/articles/favorites', { params });
    return response.data;
  },

  // Image Upload
  uploadImage: async (file: File): Promise<{ url: string; filename: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/knowledge-base/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Import
  importArticle: async (categoryId: number, file: File): Promise<KnowledgeArticle> => {
    const formData = new FormData();
    formData.append('category_id', categoryId.toString());
    formData.append('file', file);
    const response = await api.post('/api/knowledge-base/articles/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  importArticlesBatch: async (file: File): Promise<KnowledgeArticleBatchImportResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/knowledge-base/articles/import-batch', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  listArticleEditors: async (articleId: number): Promise<KnowledgeArticleEditorEntry[]> => {
    const response = await api.get(`/api/knowledge-base/admin/articles/${articleId}/editors`);
    return response.data;
  },

  grantArticleEditor: async (articleId: number, userId: number): Promise<KnowledgeArticleEditorEntry> => {
    const response = await api.post(`/api/knowledge-base/admin/articles/${articleId}/editors`, { user_id: userId });
    return response.data;
  },

  revokeArticleEditor: async (articleId: number, userId: number): Promise<void> => {
    await api.delete(`/api/knowledge-base/admin/articles/${articleId}/editors/${userId}`);
  },

  listGlobalKbEditors: async (): Promise<KnowledgeGlobalKbEditorEntry[]> => {
    const response = await api.get('/api/knowledge-base/admin/global-kb-editors');
    return response.data;
  },

  grantGlobalKbEditor: async (userId: number): Promise<KnowledgeGlobalKbEditorEntry> => {
    const response = await api.post('/api/knowledge-base/admin/global-kb-editors', { user_id: userId });
    return response.data;
  },

  revokeGlobalKbEditor: async (userId: number): Promise<void> => {
    await api.delete(`/api/knowledge-base/admin/global-kb-editors/${userId}`);
  },

  correlateAuthors: async (apply: boolean): Promise<KnowledgeAuthorCorrelationResponse> => {
    const response = await api.post(
      '/api/knowledge-base/admin/correlate-authors',
      { apply },
      { timeout: 180000 } // correlacao pode ser pesada em bases legadas
    );
    return response.data;
  },

  ask: async (question: string): Promise<KnowledgeBaseAskResponse> => {
    const response = await api.post(
      '/api/knowledge-base/ask',
      { question },
      { timeout: 120000 }
    );
    return response.data;
  },

  getAssistantVisibility: async (): Promise<KnowledgeBaseAssistantVisibilityResponse> => {
    const response = await api.get('/api/knowledge-base/assistant-visibility');
    return response.data;
  },

  updateAssistantVisibilityForGeneral: async (
    assistantVisibleForGeneral: boolean
  ): Promise<KnowledgeBaseAssistantVisibilityResponse> => {
    const response = await api.put('/api/knowledge-base/admin/assistant-visibility', {
      assistant_visible_for_general: assistantVisibleForGeneral,
    });
    return response.data;
  },
};

export const usersApi = {
  search: async (params: {
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<UserListResponse> => {
    const response = await api.get('/api/users/search', { params });
    return response.data;
  },
};

// ============================================================
// Feedback API (espelha backend/app/services/feedback)
// ============================================================
//
// Todas as funcoes aceitam `signal` opcional (AbortController) para que
// o componente que chamou possa cancelar requisicoes em desmonte
// (evita memory leak / setState em componente desmontado).

export const feedbackApi = {
  getTargets: async (
    signal?: AbortSignal,
    contextWorkflowId?: number | null,
  ): Promise<FeedbackTargetsResponse> => {
    const params =
      contextWorkflowId !== undefined && contextWorkflowId !== null
        ? { context_workflow_id: contextWorkflowId }
        : undefined;
    const response = await api.get('/api/feedbacks/targets', { signal, params });
    return response.data;
  },

  getAdminScopes: async (signal?: AbortSignal): Promise<FeedbackAdminScopesResponse> => {
    const response = await api.get('/api/feedbacks/admin-scopes', { signal });
    return response.data;
  },

  listWorkflows: async (
    signal?: AbortSignal,
    areaType?: 'tickets' | 'purchases'
  ): Promise<FeedbackWorkflowOption[]> => {
    const params = areaType ? { area_type: areaType } : undefined;
    const response = await api.get('/api/feedbacks/workflows', { signal, params });
    return response.data;
  },

  shouldPrompt: async (
    workflowId?: number | null,
    signal?: AbortSignal
  ): Promise<ShouldPromptResponse> => {
    const params: Record<string, number> = {};
    if (workflowId !== undefined && workflowId !== null) {
      params.workflow_id = workflowId;
    }
    const response = await api.get('/api/feedbacks/should-prompt', { params, signal });
    return response.data;
  },

  create: async (payload: FeedbackPayload, signal?: AbortSignal): Promise<Feedback> => {
    const response = await api.post('/api/feedbacks/', payload, { signal });
    return response.data;
  },

  list: async (
    params?: {
      scope?: string;
      workflow_id?: number;
      general_only?: boolean;
      min_rating?: number;
      max_rating?: number;
      date_from?: string;
      date_to?: string;
      is_anonymous?: boolean;
      skip?: number;
      limit?: number;
    },
    signal?: AbortSignal
  ): Promise<FeedbackListResponse> => {
    const response = await api.get('/api/feedbacks/', { params, signal });
    return response.data;
  },

  getStats: async (
    params?: {
      scope?: string;
      workflow_id?: number;
      general_only?: boolean;
      date_from?: string;
      date_to?: string;
      min_rating?: number;
      max_rating?: number;
      is_anonymous?: boolean;
      top_workflows_limit?: number;
    },
    signal?: AbortSignal
  ): Promise<FeedbackStats> => {
    const response = await api.get('/api/feedbacks/stats', { params, signal });
    return response.data;
  },

  remove: async (id: number, signal?: AbortSignal): Promise<void> => {
    await api.delete(`/api/feedbacks/${id}`, { signal });
  },

  // Prompt configs (admin)
  listPromptConfigs: async (signal?: AbortSignal): Promise<FeedbackPromptConfig[]> => {
    const response = await api.get('/api/feedbacks/prompt-configs/', { signal });
    return response.data;
  },

  upsertPromptConfig: async (
    payload: FeedbackPromptConfigPayload,
    signal?: AbortSignal
  ): Promise<FeedbackPromptConfig> => {
    const response = await api.post('/api/feedbacks/prompt-configs/', payload, { signal });
    return response.data;
  },

  removePromptConfig: async (id: number, signal?: AbortSignal): Promise<void> => {
    await api.delete(`/api/feedbacks/prompt-configs/${id}`, { signal });
  },
};

export const opportunitiesApi = {
  getModuleWorkflowMeta: async (): Promise<{ workflow_id: number }> => {
    const response = await api.get('/api/purchases/opportunities/module/workflow-meta');
    return response.data;
  },

  getModuleCustomFieldsForStep: async (): Promise<WorkflowCustomField[]> => {
    const response = await api.get('/api/purchases/opportunities/module/custom-fields/for-step');
    return response.data;
  },

  getModuleCustomFieldValues: async (): Promise<OpportunityModuleCustomFieldValue[]> => {
    const response = await api.get('/api/purchases/opportunities/module/custom-fields');
    return response.data;
  },

  putModuleCustomFieldValue: async (
    fieldId: number,
    payload: { text_value?: string | null }
  ): Promise<void> => {
    await api.put(`/api/purchases/opportunities/module/custom-fields/${fieldId}`, payload);
  },

  list: async (search?: string): Promise<Opportunity[]> => {
    const response = await api.get('/api/purchases/opportunities', { params: { search } });
    return response.data;
  },

  getById: async (opportunityId: number): Promise<Opportunity> => {
    const response = await api.get(`/api/purchases/opportunities/${opportunityId}`);
    return response.data;
  },

  getPurchaseCustomFieldValues: async (
    purchaseRequestId: number
  ): Promise<PurchaseCustomFieldValue[]> => {
    const response = await api.get(`/api/purchases/${purchaseRequestId}/custom-fields`);
    return response.data;
  },

  create: async (payload: {
    title: string;
    description?: string;
    status?: string;
    purchase_request_id?: number;
    purchase_public_id?: string;
  }): Promise<Opportunity> => {
    const response = await api.post('/api/purchases/opportunities', payload);
    return response.data;
  },

  update: async (
    opportunityId: number,
    payload: { title?: string; description?: string; status?: string }
  ): Promise<Opportunity> => {
    const response = await api.patch(`/api/purchases/opportunities/${opportunityId}`, payload);
    return response.data;
  },

  listLinkablePurchases: async (
    opportunityId: number,
    search?: string
  ): Promise<OpportunityLinkablePurchase[]> => {
    const response = await api.get(`/api/purchases/opportunities/${opportunityId}/linkable-purchases`, {
      params: { search },
    });
    return response.data;
  },

  listLinkablePurchasesForCreate: async (search?: string): Promise<OpportunityLinkablePurchase[]> => {
    const response = await api.get('/api/purchases/opportunities/linkable-purchases', {
      params: { search },
    });
    return response.data;
  },

  updatePurchaseLink: async (
    opportunityId: number,
    payload: {
      purchase_request_id?: number | null;
      purchase_public_id?: string | null;
    }
  ): Promise<Opportunity> => {
    const response = await api.patch(`/api/purchases/opportunities/${opportunityId}/purchase-link`, payload);
    return response.data;
  },

  delete: async (opportunityId: number): Promise<{ deleted_opportunity_id: number }> => {
    const response = await api.delete(`/api/purchases/opportunities/${opportunityId}`);
    return response.data;
  },

  getFolderTree: async (opportunityId: number): Promise<OpportunityFolderTreeNode[]> => {
    const response = await api.get(`/api/purchases/opportunities/${opportunityId}/folders/tree`);
    return response.data;
  },

  createFolder: async (
    opportunityId: number,
    payload: { name: string; parent_folder_id?: number | null }
  ): Promise<void> => {
    await api.post(`/api/purchases/opportunities/${opportunityId}/folders`, payload);
  },

  applyModuleDefaultFolders: async (
    opportunityId: number,
    parentFolderId: number | null
  ): Promise<{ created_count: number; skipped_count: number }> => {
    const response = await api.post(
      `/api/purchases/opportunities/${opportunityId}/folders/apply-module-defaults`,
      { parent_folder_id: parentFolderId }
    );
    return response.data;
  },

  renameFolder: async (opportunityId: number, folderId: number, name: string): Promise<void> => {
    await api.patch(`/api/purchases/opportunities/${opportunityId}/folders/${folderId}`, { name });
  },

  moveFolder: async (
    opportunityId: number,
    folderId: number,
    parentFolderId?: number | null
  ): Promise<void> => {
    await api.patch(`/api/purchases/opportunities/${opportunityId}/folders/${folderId}/move`, {
      parent_folder_id: parentFolderId ?? null,
    });
  },

  listDocuments: async (
    opportunityId: number,
    folderId?: number | null
  ): Promise<OpportunityDocument[]> => {
    const params = folderId === undefined || folderId === null ? {} : { folder_id: folderId };
    const response = await api.get(`/api/purchases/opportunities/${opportunityId}/documents`, { params });
    return response.data;
  },

  queryDocuments: async (
    opportunityId: number,
    params: {
      search?: string;
      folder_id?: number | null;
      include_subfolders?: boolean;
      scope_all?: boolean;
      skip?: number;
      limit?: number;
    }
  ): Promise<OpportunityDocumentListResponse> => {
    const response = await api.get(`/api/purchases/opportunities/${opportunityId}/documents/query`, {
      params,
    });
    return response.data;
  },

  uploadDocument: async (
    opportunityId: number,
    file: File,
    folderId?: number | null
  ): Promise<OpportunityDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId !== undefined && folderId !== null) {
      formData.append('folder_id', String(folderId));
    }
    const response = await api.post(`/api/purchases/opportunities/${opportunityId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  moveDocument: async (opportunityId: number, documentId: number, folderId?: number | null): Promise<void> => {
    await api.post(`/api/purchases/opportunities/${opportunityId}/documents/${documentId}/move`, {
      folder_id: folderId ?? null,
    });
  },

  moveDocumentsBatch: async (
    opportunityId: number,
    documentIds: number[],
    folderId?: number | null
  ): Promise<void> => {
    await api.post(`/api/purchases/opportunities/${opportunityId}/documents/move-batch`, {
      document_ids: documentIds,
      folder_id: folderId ?? null,
    });
  },

  deleteDocumentsBatch: async (
    opportunityId: number,
    documentIds: number[]
  ): Promise<{ deleted_documents: number }> => {
    const response = await api.post(`/api/purchases/opportunities/${opportunityId}/documents/delete-batch`, {
      document_ids: documentIds,
    });
    return response.data;
  },

  renameDocument: async (opportunityId: number, documentId: number, filename: string): Promise<void> => {
    await api.patch(`/api/purchases/opportunities/${opportunityId}/documents/${documentId}/rename`, { filename });
  },

  downloadDocument: async (
    opportunityId: number,
    documentId: number,
    suggestedFilename: string
  ): Promise<void> => {
    try {
      const response = await api.get(
        `/api/purchases/opportunities/${opportunityId}/documents/${documentId}/download`,
        { responseType: 'blob' }
      );
      const contentType = String(response.headers['content-type'] || '');
      if (contentType.includes('application/json')) {
        const text =
          typeof response.data?.text === 'function' ? await response.data.text() : String(response.data);
        let detail = 'Não foi possível baixar o arquivo.';
        try {
          const parsed = JSON.parse(text) as { detail?: unknown };
          if (typeof parsed.detail === 'string') {
            detail = translateKnownEnglishApiDetail(parsed.detail);
          }
        } catch {
          /* ignore */
        }
        throw new Error(detail);
      }
      const blob =
        response.data instanceof Blob ? response.data : new Blob([response.data as BlobPart]);
      const disposition = String(response.headers['content-disposition'] || '');
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;\n]+)/i);
      const asciiMatch = disposition.match(/filename="([^"]+)"/i);
      let filename = (suggestedFilename || '').trim() || `documento-${documentId}`;
      if (utf8Match?.[1]) {
        try {
          filename = decodeURIComponent(utf8Match[1].trim()) || filename;
        } catch {
          /* keep */
        }
      } else if (asciiMatch?.[1]) {
        filename = asciiMatch[1].trim() || filename;
      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const ax = e as { response?: { data?: unknown } };
      const blobData = ax?.response?.data;
      if (blobData instanceof Blob) {
        let parsedDetail: string | null = null;
        try {
          const text = await blobData.text();
          const parsed = JSON.parse(text) as { detail?: unknown };
          if (typeof parsed.detail === 'string' && parsed.detail.trim()) {
            parsedDetail = parsed.detail.trim();
          }
        } catch {
          /* corpo não é JSON */
        }
        if (parsedDetail) {
          throw new Error(parsedDetail);
        }
      }
      throw e;
    }
  },

  getFolderDeletePreview: async (opportunityId: number, folderId: number): Promise<OpportunityFolderDeletePreview> => {
    const response = await api.get(
      `/api/purchases/opportunities/${opportunityId}/folders/${folderId}/delete-preview`
    );
    return response.data;
  },

  deleteFolder: async (opportunityId: number, folderId: number): Promise<void> => {
    await api.delete(`/api/purchases/opportunities/${opportunityId}/folders/${folderId}`);
  },

  deleteDocument: async (opportunityId: number, documentId: number): Promise<void> => {
    await api.delete(`/api/purchases/opportunities/${opportunityId}/documents/${documentId}`);
  },
};


